import { PageEvent } from '@angular/material/paginator';
import { Sort } from '@angular/material/sort';
import { SafeResourceUrl } from '@angular/platform-browser';
import { Guid } from 'guid-typescript';
import { BehaviorSubject } from 'rxjs/internal/BehaviorSubject';
import { AccessGroupCarType } from 'src/app/shared/enums/access-group-car-type';
import { AppInvitationStatus } from 'src/app/shared/enums/app-invitation-status.enum';
import { ChargingSpeed } from 'src/app/shared/enums/charging-speed.enum';
import { ChargingUserStatus } from 'src/app/shared/enums/charging-user-status.enum';
import { DiscountType } from 'src/app/shared/enums/discount-type.enum';
import { IdTagUserType } from 'src/app/shared/enums/id-tag-user-type.enum';
import { AccessGroupUserType } from '../../shared/enums/access-group-user-type';

export interface ChargingUser {
  id: number;
  identifier: string;
  name: string;
  emailAddress: string;
  phoneNumber: string;
  organizationId: number;
  departmentId: number;
  accessGroupCount: number;
  timeLimitedAccessGroupCount: number;
  previousAccessGroupCount: number;
  cprNumber: string;
  bankRegistrationNumber: string;
  bankAccountNumber: string;
  idTagUserType: IdTagUserType;
  chargingUserStatus: ChargingUserStatus;
  invoicingAccountNumber: number;
  agreementNumber: number;
  idTagShippingAddress: string;
  departmentAddress: string;
  printedIds: string;
  created: Date;
  createdBy: string;
  updated: Date;
  updatedBy: string;
  organizationName: string;
  departmentName: string;
  highlight: BehaviorSubject<boolean>;
  idTagCount: number;
  deleted: Date;
  deletedBy: string;
  isDeleted: boolean;
  mokUserNumber?: number;
  deviceId?: string;
  alias?: string;
  appInvitationHasBeenSent: boolean;
  phoneCountryCode: number;
  darId?: Guid;
}

export class ChargingUserSearchEvent {
  constructor(paging: PageEvent) {
    this.paging = paging;
    this.sorting = null;
    this.chargingUserStatuses = [];
    this.excludedAccessGroupIdentifier = null;
  }
  organizationId?: number | null;
  departmentId?: number;
  query?: string | null;
  chargingUserStatuses?: ChargingUserStatus[];
  idTagUserType?: IdTagUserType;
  chargingUserId?: number;
  excludedAccessGroupIdentifier?: string | null;
  paging: PageEvent;
  sorting?: Sort | null;
}

export interface UpdateChargingUserAddress {
  fullAddress: string;
  darId: string;
}
export interface DownloadChargingUserReportRequest {
  from: string;
  to: string;
  organizationIds: number[];
  idTagUserType: IdTagUserType;
}

export interface UpdateIdTagInformationRequest {
  idTagUserType?: IdTagUserType;
  invoicingAccountNumber: number | null;
  agreementNumber: number | null;
  shippingAddress: string | null;
}
export interface UpdateIdTagShippingAddress {
  shippingAddress: string;
  darId: string;
}

export interface ChargingUserAppInvitation {
  id: number;
  chargingUserId: number;
  chargingUserIdentifier: string;
  chargingUsersName: string;
  identifier: string;
  phoneNumber: string;
  deepLink: string;
  appInvitationStatus: AppInvitationStatus;
  expirationInSeconds: number;
  created: Date;
  createdBy: string;
  updated: Date;
  updatedBy: string;
  deepLinkCreated: Date | null;
  smsSent: Date | null;
  allowSmsResend: boolean;
  timeLimitFrom: Date | null;
  timeLimitTo: Date | null;
  executionDate?: Date;
  phoneCountryCode: number;
}

export interface ScheduleAppInvitationRequest {
  PhoneNumber: string;
  ExecutionDate: Date;
  phoneCountryCode: number;
}

export class AppInvitationsSearchEvent {
  constructor(paging: PageEvent) {
    this.paging = paging;
    this.sorting = null;
  }
  query?: string | null;
  appInvitationStatus?: AppInvitationStatus;
  chargingUserIdentifier?: string | null;
  paging: PageEvent;
  sorting?: Sort | null;
}

export interface AccessGroup {
  identifier: string;
  chargingUserIdentifier: string;
  name: string;
  amount: number;
  discountType: DiscountType;
  accessType: AccessGroupUserType;
  timeLimitedFrom?: Date;
  timeLimitedTo?: Date;
  deleted?: Date;
  mokUserNumber?: number;
  deviceId?: string;
  accessThrough: AccessThrough;
}

export enum AccessThrough {
  ChargingUserIdentifier = 'ChargingUserIdentifier',
  MokUserNumber = 'MokUserNumber',
  DeviceId = 'DeviceId',
}

export class AccessGroupUsersSearchEvent {
  constructor(paging: PageEvent) {
    this.paging = paging;
    this.sorting = null;
  }
  chargingUserIdentifier?: string | null;
  deviceId?: string | null;
  mokUserNumber?: number | null;
  paging: PageEvent;
  sorting?: Sort | null;
  includeDeleted?: boolean;
}

export interface ChargingUserAccessGroup {
  accessGroups: AccessGroup[];
}

export interface PreviousAccessGroup {
  name: string;
  created: Date;
  deleted?: Date | null;
  discountAmount?: number;
  discountType?: DiscountType;
  accessType: AccessGroupUserType;
  activeFrom?: Date;
  activeTo?: Date | null;
}

export class PreviousAccessGroupsSearchEvent {
  constructor(paging: PageEvent) {
    this.paging = paging;
  }
  chargingUserId?: number;
  paging: PageEvent;
}

export interface AccessGroup {
  accessGroupIdentifier: string;
  accessGroupChargingUserIdentifier: string;
  accessGroupName: string;
  organizationName: string;
  organizationLogoLink: string;
  compactOrganizationLogoLink: string;
  connectorCount: number;
  locations: Location[];
  activeFrom: string; // ISO timestamp
  activeTo: string; // ISO timestamp
  carType: AccessGroupCarType;
}

export interface Location {
  locationId: string;
  name: string;
  address: string;
  longitude: number;
  latitude: number;
  maxPower: number;
  chargingSpeed: ChargingSpeed;
  safeMapUrl: SafeResourceUrl;
}
export interface InvitationDetails {
  organizationName: string;
  compactOrganizationLogoLink: string;
  organizationLogoLink: string;
  activeFrom: string; // ISO date string
  activeTo: string; // ISO date string
  appInvitationStatus: AppInvitationStatus;
}
